import ScopeAndSequence from '@/common/data/scopeAndSequence.json';
import Scripts from '@/common/data/scripts.json';
import {
  Concepts,
  DialogueSlide,
  ScriptItem,
} from '@/common/types/LectioTypes';
// import WorldNode, { NodeStatus } from '@/game/WorldNode';
// import {
//   Code,
//   Customization,
//   SentenceItem,
//   WordItem,
// } from '../../../../common/types/LectioTypes';

import { CreateAttempt } from '@/common/types/session';
import { Student } from '@/common/types/student';
import { sanitize } from '@/common/utils/helpers';
import { studentIsDev } from '@/common/utils/student';
import thresholdsData from '@/game/GameComponents/GameData/minigameSpeedThresholds.json';
import {
  GameInstance,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import { isSizedElement } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/LayoutGroupUtils';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import { NodeStatus } from '@/game/prefabs/World/WorldNode';
import _ from 'lodash';
import AnchorPlugin from 'phaser3-rex-plugins/plugins/anchor-plugin';
import { MinigameSpeedThresholds } from '../Types/IMinigameSpeedThreshold';

const minigameSpeedThresholds = thresholdsData as MinigameSpeedThresholds;

interface DebugColorConfig {
  tileColor?: Phaser.Display.Color;
  collidingTileColor?: Phaser.Display.Color;
  faceColor?: Phaser.Display.Color;
}

export function renderCollisionDebug(
  scene: Phaser.Scene,
  collisionLayer: Phaser.Tilemaps.TilemapLayer,
  config?: DebugColorConfig,
) {
  const defaultConfig = {
    tileColor: null, // Color for non-colliding tiles
    collidingTileColor: new Phaser.Display.Color(243, 134, 48, 255), // Color for colliding tiles
    faceColor: new Phaser.Display.Color(40, 39, 37, 255), // Color for colliding faces
  };

  const finalConfig = { ...defaultConfig, ...config };

  const debugGraphics = scene.add.graphics().setAlpha(0.75);
  collisionLayer.renderDebug(debugGraphics, {
    tileColor: finalConfig.tileColor,
    collidingTileColor: finalConfig.collidingTileColor,
    faceColor: finalConfig.faceColor,
  });
}

export function setBitmapColor(
  text: Phaser.GameObjects.BitmapText,
  color: number,
) {
  text.tintTopLeft = color;
  text.tintTopRight = color;
  text.tintBottomLeft = color;
  text.tintBottomRight = color;
}

export function animateBitmapColourTransition(
  text: Phaser.GameObjects.BitmapText,
  endColour: number,
  returnColour: number,
  duration: number,
  returnToOriginalColour = true,
): Phaser.Tweens.Tween {
  const startColor = text.tintTopLeft;
  const startColorRgb = hexNumberToRgb(startColor);
  const endColorRgb = hexNumberToRgb(endColour);
  const returnColorRgb = hexNumberToRgb(returnColour);

  return text.scene.tweens.add({
    targets: {
      r: startColorRgb.r,
      g: startColorRgb.g,
      b: startColorRgb.b,
    },
    r: endColorRgb.r,
    g: endColorRgb.g,
    b: endColorRgb.b,
    duration,
    ease: 'Power2',
    onUpdate: function (_tween, targets) {
      const hexColour = rgbToHexNumber(targets.r, targets.g, targets.b);
      setBitmapColor(text, hexColour);
    },
    onComplete: function () {
      if (!returnToOriginalColour) return;
      // Return to original colour.
      text.scene.tweens.add({
        targets: {
          r: endColorRgb.r,
          g: endColorRgb.g,
          b: endColorRgb.b,
        },
        r: returnColorRgb.r,
        g: returnColorRgb.g,
        b: returnColorRgb.b,
        duration,
        ease: 'Power2',
        onUpdate: function (_tween, targets) {
          const hexColour = rgbToHexNumber(targets.r, targets.g, targets.b);
          setBitmapColor(text, hexColour);
        },
      });
    },
  });
}

export function hexNumberToRgb(hexNum: number) {
  const r = (hexNum >> 16) & 0xff;
  const g = (hexNum >> 8) & 0xff;
  const b = hexNum & 0xff;

  return { r, g, b };
}

export function rgbToHexNumber(r: number, g: number, b: number) {
  r = Math.max(0, Math.min(255, Math.round(r)));
  g = Math.max(0, Math.min(255, Math.round(g)));
  b = Math.max(0, Math.min(255, Math.round(b)));

  return (r << 16) | (g << 8) | b;
}

export function getBiomeFromStudentLevel(level: number) {
  const LEVELS_PER_BIOME = 5;
  const TOTAL_BIOMES = 5;
  return Math.floor((level - 1) / LEVELS_PER_BIOME) % TOTAL_BIOMES;
}

export const biomeKeys = ['forest', 'spring', 'desert', 'winter', 'tropical'];

export function sanitizeFilename(
  filename: string,
  replacement: string = '_',
): string {
  // eslint-disable-next-line no-control-regex
  const invalidChars = /[<>:"/\\|?*\x00-\x1F]/g; // Includes control characters (0-31)
  return filename.replace(invalidChars, replacement);
}

export function containsAlphabeticalContent(s: string) {
  let r = /[a-zA-Z]/g;
  return s && s.match(r);
}

export function isLetter(s: string) {
  return s.length === 1 && containsAlphabeticalContent(s);
}

export function removePunctuation(s: string) {
  let res = s;
  res = res.replace(/[^\w\s.]/g, '');
  res = res.replace(/(?<!\w)\./g, '');
  return res;
}

export function getLessonPanelSlides() {
  return [getNewLevelScript()] as ScriptItem[];
}

function getNewLevelScript() {
  const ssLevel = getStudent().level;
  const level = ScopeAndSequence.levels.find((l) => l.level === ssLevel);
  if (level === undefined) return null;
  return Scripts['levelScripts'].find((s) => s.id === level.id) as ScriptItem;
}

export function createDialogueSlideFromConcept(conceptKey: string) {
  const concepts = Scripts['concepts'] as Concepts;
  return {
    script: concepts[`${conceptKey}`].explanation || '',
  } as DialogueSlide;
}

const emphasizedLetterRegex = /<(.*?)>/g;
const morphemeRegex = /\[(.*)\]/g;
const phonemeRegex = /\/(.*?)\//g;
const relatedConceptRegex = /\*\s*(.*?)\s*\*\s*\(\s*(.*?)\s*\)/g;
const imageLinkRegex = /&(.*?)&\((.*?)\)/g;

export function isEmphasizedLetter(content: string) {
  return content.match(emphasizedLetterRegex);
}

export function isMorpheme(content: string) {
  return content.match(morphemeRegex);
}

export function isPhoneme(content: string) {
  return content.match(phonemeRegex);
}

export function isRelatedConceptLink(content: string) {
  return content.match(relatedConceptRegex);
}

export function isImageLink(content: string) {
  return content.match(imageLinkRegex);
}

export function sanitizeLessonMarkup(content: string) {
  if (isEmphasizedLetter(content)) {
    return content.replace(emphasizedLetterRegex, '$1');
  } else if (isMorpheme(content)) {
    return content.replace(morphemeRegex, '$1');
  } else if (isPhoneme(content)) {
    // Create a placeholder image.
    return '      ';
  } else if (isRelatedConceptLink(content)) {
    return content.replace(relatedConceptRegex, '$1');
  } else if (isImageLink(content)) {
    return content.replace(imageLinkRegex, '$1');
  }
  return content;
}

export function getRelatedConceptFromMarkup(content: string) {
  return content.replace(relatedConceptRegex, '$2');
}

export interface DynamicTextWrapper {
  textObj: Phaser.GameObjects.DynamicBitmapText;
  rawText: string;
}

export function underlineBitmapText(
  scene: Phaser.Scene,
  parent: Phaser.GameObjects.Container,
  text: Phaser.GameObjects.DynamicBitmapText,
  color: number,
) {
  // Get text width
  const textWidth = text.width;
  const textHeight = text.height;

  // Create an underline (rectangle)
  const underline = scene.add.graphics();
  underline.fillStyle(color, 1); // underline
  underline.fillRect(text.x, text.y + textHeight - 5, textWidth, 2); // Position it below text
  parent.add(underline);
  return underline;
}

export interface DynamicTextConfig {
  maxWidth: number;
  fontSize: number;
  textColor: number;
}

export function createDynamicTextLayout(
  content: string[],
  scene: Phaser.Scene,
  parent: Phaser.GameObjects.Container,
  config: DynamicTextConfig,
) {
  function widthOfLine(line: number) {
    let lineElements = textArray[line];
    let totalWidth = 0;
    lineElements.forEach((lineElement) => {
      totalWidth += lineElement.textObj.width;
    });
    return totalWidth;
  }

  function getTextPos() {
    const yPos = yIndex * lineSpace;
    const xPos = widthOfLine(yIndex);
    return { x: xPos, y: yPos };
  }

  function getIsOverflowing(
    element: Phaser.GameObjects.DynamicBitmapText,
    line: number,
    maxLineWidth: number,
  ) {
    const lineWidth = widthOfLine(line);
    return lineWidth + element.width > maxLineWidth;
  }

  function addNewLine() {
    yIndex++;
    textArray[yIndex] = [];
    xIndex = 0;
  }

  function getTintForDynamicText(content: string) {
    if (
      isPhoneme(content) ||
      isMorpheme(content) ||
      isEmphasizedLetter(content)
    ) {
      return 0x693cbd;
    }
    return config.textColor;
  }

  const textArray: DynamicTextWrapper[][] = [];
  let xIndex = 0;
  let yIndex = 0;
  const lineSpace = 24;
  const placeholderIcons = [];

  for (let i = 0; i < content.length; i++) {
    // Initialize a new line if necessary.
    if (!textArray[yIndex]) {
      textArray[yIndex] = [];
    }
    let rawC = content[i];
    let c = sanitizeLessonMarkup(rawC);

    // Special case for phonemes. Use an icon instead.
    if (c === '      ') {
      const offset = new Phaser.Math.Vector2(16, 16);
      const posX = getTextPos().x + offset.x;
      const posY = getTextPos().y + offset.y;
      const placeholder = scene.add.image(posX, posY, 'phoneme-graphic');
      placeholder.setScale(1.5);
      parent.add(placeholder);
      placeholderIcons[i] = placeholder;
      placeholder.flipX = true;
    }

    let newTextPos = getTextPos();
    let newDynamicText = scene.add.dynamicBitmapText(
      newTextPos.x,
      newTextPos.y,
      'Aronui',
      c,
    );
    const color = getTintForDynamicText(rawC);
    newDynamicText.setTint(color);
    newDynamicText.setFontSize(config.fontSize);
    parent.add(newDynamicText);

    // Check if the newly added text overflowed:
    const overflowed = getIsOverflowing(
      newDynamicText,
      yIndex,
      config.maxWidth,
    );
    if (overflowed) {
      // cut the text off until it fits.
      let wordIndex = 0;
      const contentSplit = c.split(' ');
      newDynamicText.text = contentSplit.slice(0, wordIndex).join(' ');
      while (
        !getIsOverflowing(newDynamicText, yIndex, config.maxWidth) &&
        wordIndex < contentSplit.length
      ) {
        newDynamicText.text = contentSplit.slice(0, wordIndex).join(' ');
        wordIndex++;
      }
      // We made the text overflow. Go back 1 word.
      newDynamicText.text = contentSplit.slice(0, wordIndex - 2).join(' ');
      const remainingText = contentSplit.slice(wordIndex - 2).join(' ');
      // Add the remaining content to the content array.
      content.splice(i + 1, 0, remainingText);
    }

    textArray[yIndex][xIndex] = { textObj: newDynamicText, rawText: rawC };
    xIndex++;

    if (overflowed) {
      // Update indices.
      addNewLine();
    }
  }

  return { textArray, placeholderIcons };
}

export function DoReinforcementFeedback(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.Components.Transform,
) {
  // Original position to return to
  const originalX = target.x;
  const originalY = target.y;

  // Shake parameters - adjust these to change the feel
  const intensity = 7; // Maximum shake distance
  const duration = 400; // Total duration in ms
  const ease = 'Sine.easeInOut';
  const repeat = 2; // Number of shakes

  // Create the shake tween
  scene.tweens.add({
    targets: target,
    x: originalX + intensity,
    duration: duration / (repeat * 2),
    yoyo: true,
    repeat: repeat,
    ease: ease,
    onComplete: () => {
      // Reset to original position when complete
      target.setPosition(originalX, originalY);
    },
    onUpdate: () => {
      // Add some randomness to make it more natural
      if (Math.random() > 0.5) {
        target.x += (Math.random() * intensity) / 4 - intensity / 8;
      }
    },
  });
  PhaserAudioManager.playSFX(scene, 'card-reveal-2');
}

export function DoPositiveFeedback(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.Components.Transform,
  options: {
    feedbackDuration?: number;
    heightModifier?: number;
  },
) {
  // Store the original Y position
  const originalY = target.y;
  // Create the bounce tween
  scene.tweens.add({
    targets: target,
    y: originalY - 20 * (options.heightModifier || 1), // Bounce up by 20 pixels
    duration: (options.feedbackDuration || 400) / 1.5, // Quick upward movement
    ease: 'Power2',
    yoyo: true, // Automatically reverse the tween
    onComplete: () => {
      // Ensure the card ends up at exactly the original position
      target.y = originalY;
      scene.tweens.add({
        targets: target,
        y: originalY - 7, // Bounce up by 7 pixels
        duration: (options.feedbackDuration || 400) / 2.5, // Quick upward movement
        ease: 'Power2',
        yoyo: true, // Automatically reverse the tween
        onComplete: () => {
          // Ensure the card ends up at exactly the original position
          target.y = originalY;
        },
      });
    },
  });
}

export function getAnchorPlugin(scene: Phaser.Scene): AnchorPlugin {
  const anchor = scene.plugins.get('rexanchorplugin') as AnchorPlugin | null;
  if (anchor === null) {
    throw new Error('Anchor plugin not loaded');
  }
  return anchor;
}

export function getNextGame() {
  const { enabledGames, gamesProgress, completedPlaythroughs } =
    GameInstance.instance.getActiveLevelProgress();
  return enabledGames.find(
    (game) => (gamesProgress[game]?.timesPlayed ?? 0) === completedPlaythroughs,
  );
}

export type SizedObject =
  | Phaser.GameObjects.Container
  | Phaser.GameObjects.NineSlice
  | Phaser.GameObjects.Image
  | Phaser.GameObjects.Sprite
  | StudentGameCard
  | Phaser.GameObjects.BitmapText;

export function getNodeStatus(nodeIndex: number) {
  const { gamesProgress, completedPlaythroughs } =
    GameInstance.instance.getActiveLevelProgress();
  const associatedGame = Object.values(gamesProgress).find(
    (gameProgress) => gameProgress.completedAtNodeIndex === nodeIndex,
  );
  return associatedGame &&
    (associatedGame.timesPlayed ?? 0) > completedPlaythroughs
    ? NodeStatus.COMPLETE
    : NodeStatus.REMAINING;
}

export function getFinalRubiesFromAttemptsAndAccuracy(
  attempts: CreateAttempt[],
  accuracy: number,
) {
  const maxRubies = 60;
  const rubiesPerSpeedBonusAttempt = 2;
  const numSpeedBonusAttempts = attempts.filter(
    didAttemptEarnSpeedBonus,
  ).length;

  const baseReward = (accuracy / 100) * maxRubies;
  const speedBonus = numSpeedBonusAttempts * rubiesPerSpeedBonusAttempt;

  return Math.floor(baseReward + speedBonus);
}

export function countUp(
  text: Phaser.GameObjects.BitmapText,
  value: number,
  duration: number,
  onFinished?: () => void,
  prefix?: string,
  suffix?: string,
) {
  text.text = `${prefix}${value}${suffix}`;
  prefix = prefix ?? '';
  suffix = suffix ?? '';
  let countTime = 0;
  const interval = 25;
  const countUp = setInterval(() => {
    countTime += interval;
    text.text = `${prefix}${Math.floor(
      (countTime / duration) * value,
    )}${suffix}`;
    if (countTime >= duration) {
      clearInterval(countUp);
      if (onFinished) {
        onFinished();
      }
    }
  }, interval);
}

export function getDistanceBetweenPoints(
  point1: Phaser.Math.Vector2,
  point2: Phaser.Math.Vector2,
) {
  return Phaser.Math.Distance.Between(point1.x, point1.y, point2.x, point2.y);
}

export function setFilterModeForAllTextures(
  scene: Phaser.Scene,
  filterMode: Phaser.Textures.FilterMode,
) {
  const textureKeys = scene.textures.getTextureKeys();
  textureKeys.forEach((key) => {
    const texture = scene.textures.get(key);
    if (texture.source && texture.source[0]) {
      texture.source[0].setFilter(filterMode);
    }
  });
}

export function vectorDistance(a: number[], b: number[]): number {
  if (a.length !== b.length) {
    throw new Error('Vectors must have the same length');
  }

  let sum = 0;
  for (let i = 0; i < a.length; i++) {
    sum += Math.pow(a[i] - b[i], 2);
  }

  return Math.sqrt(sum);
}

export function shouldShowWordHelperButtons(game: string) {
  return (
    game === 'phonemeForge' ||
    game === 'wordWeaver' ||
    game === 'wordSums' ||
    game === 'morphMaster'
  );
}

export function shouldShowGraphemeExampleWordButton(game: string) {
  return game === 'mysticMatch';
}

export function getScreenPosition(
  gameObject: Phaser.GameObjects.Components.Transform,
) {
  return {
    x: gameObject.getWorldTransformMatrix().tx,
    y: gameObject.getWorldTransformMatrix().ty,
  };
}

export function getArticulatoryGestureTexture(gesture: string) {
  const randomTone = _.sample(['', '_1', '_2']);
  return `${gesture}${randomTone}`;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function isAlphabetical(char: string): boolean {
  return /^[a-zA-Z]$/.test(char);
}

export async function waitForSeconds(
  time: Phaser.Time.Clock,
  seconds: number,
): Promise<void> {
  return new Promise((resolve) => time.delayedCall(seconds * 1000, resolve));
}

export async function waitForMilliseconds(
  time: Phaser.Time.Clock,
  milliseconds: number,
): Promise<void> {
  return new Promise((resolve) => time.delayedCall(milliseconds, resolve));
}

export async function waitUntil(
  time: Phaser.Time.Clock,
  condition: () => boolean | Promise<boolean>,
  timeout: number = Number.MAX_SAFE_INTEGER,
  interval: number = 100,
): Promise<void> {
  const startTime = Date.now();

  while (true) {
    // Check if condition is met
    if (await condition()) {
      return;
    }

    // Check for timeout
    if (Date.now() - startTime >= timeout) {
      throw new Error('Timeout waiting for condition');
    }

    // Wait before checking again
    await new Promise((resolve) => time.delayedCall(interval, resolve));
  }
}

export function isPhaserDevMode(student: Student) {
  return process.env.NODE_ENV === 'development' || studentIsDev(student);
}

export function didAttemptEarnSpeedBonus(attempt: CreateAttempt) {
  const thresholds = minigameSpeedThresholds as MinigameSpeedThresholds;
  const speedBonusThreshold =
    thresholds[attempt.activityName]?.[attempt.control] ?? 0;

  const attemptTimeInSeconds = (attempt.endedAt - attempt.startedAt) / 1000;
  return (
    sanitize(attempt.correctAnswer) === sanitize(attempt.studentAnswer) &&
    attemptTimeInSeconds <= speedBonusThreshold
  );
}

export function shouldSubmitOnPointerUp(e: any): boolean {
  if (!e.downX || !e.upX || !e.downY || !e.upY) return false;

  const downPos = new Phaser.Math.Vector2(e.downX, e.downY);
  const upPos = new Phaser.Math.Vector2(e.upX, e.upY);
  const distance = Phaser.Math.Distance.BetweenPoints(downPos, upPos);
  return distance < 30;
}

export function getVisibleBounds(container: Phaser.GameObjects.Container) {
  let minX = Infinity,
    minY = Infinity;
  let maxX = -Infinity,
    maxY = -Infinity;

  container.iterate((child: any) => {
    if (!child.visible) return;

    const bounds = child.getBounds();
    minX = Math.min(minX, bounds.x);
    minY = Math.min(minY, bounds.y);

    const childW = isSizedElement(child)
      ? child.getElementSize().x
      : child.getBounds().width;
    const childH = isSizedElement(child)
      ? child.getElementSize().y
      : child.getBounds().height;

    maxX = Math.max(maxX, bounds.x + childW);
    maxY = Math.max(maxY, bounds.y + childH);
  });

  if (minX === Infinity) {
    // No visible children
    return new Phaser.Geom.Rectangle(container.x, container.y, 0, 0);
  }

  return new Phaser.Geom.Rectangle(minX, minY, maxX - minX, maxY - minY);
}
